import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { value: "general",   label: "General question" },
  { value: "feedback",  label: "Comment / feedback" },
  { value: "billing",   label: "Billing or subscription" },
  { value: "bug",       label: "Bug or technical issue" },
  { value: "privacy",   label: "Privacy / data request" },
  { value: "other",     label: "Other" },
];

export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    category: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please give us a few more words so we can help.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        category: form.category,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Got it — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-newsprint" data-testid="support-page">
      {/* Dateline strip */}
      <div className="border-b border-[var(--border)] bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600">
          <Link to="/" className="hover:text-black flex items-center gap-2" data-testid="support-home-link">
            <span className="brand-mark"><span /><span /><span /></span>
            <span className="font-display text-[14px] font-semibold tracking-[-0.02em]">Fount Stories</span>
          </Link>
          <span>Support</span>
          <Link to={user ? "/app" : "/login"} className="hover:text-black">
            {user ? "Open app" : "Sign in"}
          </Link>
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid grid-cols-1 md:grid-cols-[1.1fr,1fr] gap-12">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-gray-700 mb-4">
            Talk to a human
          </div>
          <h1 className="font-display text-5xl lg:text-6xl tracking-[-0.03em] font-medium leading-[0.95]">
            Questions, comments,<br /><span className="text-gradient">or concerns.</span>
          </h1>
          <p className="text-base text-gray-700 leading-relaxed mt-6 max-w-md">
            Whatever it is — a billing question, a bug, a feature wish, or a note about how Fount
            is working for your show — drop it in the form. A real human on the team replies within
            one business day.
          </p>

          <div className="mt-10 space-y-3">
            <div className="flex items-start gap-3">
              <span className="icon-tile shrink-0" style={{ background: "#EFE6FE" }}>
                <Mail size={18} strokeWidth={1.8} color="#7C3AED" />
              </span>
              <div>
                <div className="font-display text-base tracking-tight">Prefer email?</div>
                <a href="mailto:support@fountstories.com" className="text-sm text-gray-600 hover:text-black">
                  support@fountstories.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-[var(--border)] bg-white p-6 md:p-8 shadow-[0_24px_64px_-32px_rgba(124,58,237,0.20)]">
          {done ? (
            <div className="text-center py-10" data-testid="support-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#EFE6FE] mb-4">
                <CheckCircle2 size={28} strokeWidth={1.8} color="#7C3AED" />
              </div>
              <h2 className="font-display text-2xl tracking-tight font-medium">Thanks — note received.</h2>
              <p className="text-sm text-gray-600 mt-2 max-w-xs mx-auto">
                We sent a confirmation to <span className="font-medium text-gray-900">{form.email}</span>.
                A human will follow up within one business day.
              </p>
              <button
                onClick={() => { setDone(false); setForm((f) => ({ ...f, subject: "", message: "" })); }}
                className="mt-6 text-[11px] font-mono uppercase tracking-[0.25em] text-gray-600 hover:text-black"
                data-testid="support-send-another"
              >
                Send another note →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="support-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Your name">
                  <input
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Jane Storyteller"
                    className="input"
                    data-testid="support-name"
                  />
                </Field>
                <Field label="Your email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@studio.com"
                    className="input"
                    data-testid="support-email"
                  />
                </Field>
              </div>

              <Field label="What is this about?">
                <select
                  value={form.category}
                  onChange={onChange("category")}
                  className="input"
                  data-testid="support-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Subject">
                <input
                  required
                  value={form.subject}
                  onChange={onChange("subject")}
                  placeholder="Short summary of your note"
                  className="input"
                  data-testid="support-subject"
                />
              </Field>

              <Field label="Your message">
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell us as much as you can — context, expected behavior, or what you wish Fount did."
                  className="input resize-y min-h-[140px]"
                  data-testid="support-message"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                data-testid="support-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-black text-white hover:bg-gray-900 disabled:opacity-60 rounded-xl px-6 py-3.5 font-medium text-sm transition-colors"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} strokeWidth={1.8} />}
                {submitting ? "Sending…" : "Send note"}
              </button>

              <p className="text-[11px] text-gray-500 leading-relaxed">
                By submitting you agree to our{" "}
                <Link to="/privacy" className="underline hover:text-black">Privacy Policy</Link>.
                We only use your email to reply to your note.
              </p>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-[var(--border)] bg-white/70 px-6 lg:px-10 py-6 text-[11px] font-mono uppercase tracking-widest text-gray-600 flex items-center justify-between gap-4 flex-wrap">
        <span>Fount Stories · Signal for storytellers</span>
        <div className="flex items-center gap-6">
          <Link to="/pricing" className="hover:text-black">Pricing</Link>
          <Link to="/support" className="hover:text-black">Support</Link>
          <Link to="/refund-policy" className="hover:text-black">Refunds</Link>
          <Link to="/terms" className="hover:text-black">Terms</Link>
          <Link to="/privacy" className="hover:text-black">Privacy</Link>
        </div>
      </footer>

      <style>{`
        .input {
          width: 100%;
          background: #FAF7F1;
          border: 1px solid rgba(0,0,0,0.10);
          border-radius: 12px;
          padding: 11px 13px;
          font-size: 14px;
          color: #111;
          transition: border-color .15s ease, background .15s ease;
        }
        .input:focus {
          outline: none;
          border-color: #111;
          background: #fff;
        }
        .input::placeholder { color: #9b958a; }
        select.input {
          appearance: none;
          background-image: linear-gradient(45deg, transparent 50%, #888 50%), linear-gradient(135deg, #888 50%, transparent 50%);
          background-position: calc(100% - 18px) 16px, calc(100% - 13px) 16px;
          background-size: 5px 5px, 5px 5px;
          background-repeat: no-repeat;
          padding-right: 32px;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
